import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const footerLinkClassName =
  'flex min-h-11 items-center text-xs transition-colors hover:text-foreground sm:text-sm'

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="relative mt-8 border-t border-border bg-card/50 px-4 pt-10 pb-8 text-xs text-muted-foreground sm:mt-10 sm:px-6 sm:pb-10 sm:text-sm md:mt-12 md:px-10 md:pt-12 md:pb-12 lg:px-16">
      {/* Decorative gradient */}
      <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-border to-transparent" />

      <div className="mx-auto max-w-7xl">
        <div className="sm:hidden">
          <div>
            <h3 className="text-sm font-semibold text-foreground">IQDOC</h3>
            <p className="mt-3 max-w-sm text-xs leading-relaxed text-foreground/70">
              {t('FOOTER_DESCRIPTION')}
            </p>
            <p className="mt-4 text-xs font-semibold text-foreground/80">
              IQDOC × MEDACH
            </p>
          </div>

          <div className="mt-7 border-t border-border">
            <details className="group border-b border-border">
              <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 py-3 text-sm font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                {t('FOOTER_NAV_TITLE')}
                <ChevronDown
                  aria-hidden="true"
                  className="size-4 transition-transform group-open:rotate-180"
                />
              </summary>
              <nav
                aria-label={t('FOOTER_NAV_TITLE')}
                className="grid grid-cols-2 gap-x-5 pb-4"
              >
                <Link to="/about" className={footerLinkClassName}>
                  {t('LANDING_NAV_ABOUT')}
                </Link>
                <Link to="/research" className={footerLinkClassName}>
                  {t('LANDING_NAV_RESEARCH')}
                </Link>
                <Link to="/calculators" className={footerLinkClassName}>
                  {t('LANDING_NAV_CALCULATORS')}
                </Link>
                <Link to="/pricing" className={footerLinkClassName}>
                  {t('LANDING_NAV_PRICING')}
                </Link>
                <Link to="/posts" className={footerLinkClassName}>
                  {t('LANDING_NAV_NEWS')}
                </Link>
                <Link to="/contacts" className={footerLinkClassName}>
                  {t('LANDING_NAV_CONTACTS')}
                </Link>
              </nav>
            </details>

            <section className="border-b border-border py-5">
              <h3 className="text-sm font-semibold text-foreground">
                {t('FOOTER_DOCS_TITLE')}
              </h3>
              <nav aria-label={t('FOOTER_DOCS_TITLE')} className="mt-2">
                <a
                  href="/legal/agreement-service-usage-individuals.pdf"
                  target="_blank"
                  rel="noopener"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_DOCS_USER_AGREEMENT')}
                </a>
                <a
                  href="/legal/policy-personal-data-processing.pdf"
                  target="_blank"
                  rel="noopener"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_DOCS_DATA_POLICY')}
                </a>
                <a
                  href="/legal/policy-acceptable-use.pdf"
                  target="_blank"
                  rel="noopener"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_DOCS_USAGE_POLICY')}
                </a>
                <a
                  href="/legal/consent-personal-data-processing.pdf"
                  target="_blank"
                  rel="noopener"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_DOCS_DATA_CONSENT')}
                </a>
                <a
                  href="/legal/agreement-third-party-transfer.pdf"
                  target="_blank"
                  rel="noopener"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_DOCS_THIRD_PARTY')}
                </a>
              </nav>
            </section>

            <details className="group border-b border-border">
              <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 py-3 text-sm font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                {t('FOOTER_CONTACTS_TITLE')}
                <ChevronDown
                  aria-hidden="true"
                  className="size-4 transition-transform group-open:rotate-180"
                />
              </summary>
              <div className="pb-4">
                <a
                  href="https://t.me/iqdocai_support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_CONTACTS_TELEGRAM')}
                </a>
                <a href="mailto:admin@iqdoc.ai" className={footerLinkClassName}>
                  {t('FOOTER_CONTACTS_EMAIL')}
                </a>
                <a href="mailto:sales@iqdoc.ai" className={footerLinkClassName}>
                  {t('FOOTER_CONTACTS_COLLABORATION')}
                </a>
                <a
                  href="mailto:support@iqdoc.ai"
                  className={footerLinkClassName}
                >
                  {t('FOOTER_CONTACTS_SUPPORT')}
                </a>
              </div>
            </details>
          </div>
        </div>

        <div className="mb-8 hidden grid-cols-1 gap-8 sm:mb-10 sm:grid sm:grid-cols-2 sm:gap-10 md:mb-12 md:gap-12 lg:grid-cols-4">
          {/* About */}
          <div className="space-y-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground sm:text-base">
              IQDOC
            </h3>
            <p className="max-w-xs text-xs leading-relaxed text-foreground/70 sm:text-sm">
              {t('FOOTER_DESCRIPTION')}
            </p>
            <div className="pt-2">
              <span className="text-xs font-semibold text-foreground/80">
                IQDOC × MEDACH
              </span>
            </div>
          </div>

          {/* Navigation */}
          <div className="space-y-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground sm:text-base">
              {t('FOOTER_NAV_TITLE')}
            </h3>
            <nav aria-label={t('FOOTER_NAV_TITLE')} className="space-y-2.5">
              <Link to="/about" className={footerLinkClassName}>
                {t('LANDING_NAV_ABOUT')}
              </Link>
              <Link to="/research" className={footerLinkClassName}>
                {t('LANDING_NAV_RESEARCH')}
              </Link>
              <Link to="/calculators" className={footerLinkClassName}>
                {t('LANDING_NAV_CALCULATORS')}
              </Link>
              <Link to="/pricing" className={footerLinkClassName}>
                {t('LANDING_NAV_PRICING')}
              </Link>
              <Link to="/posts" className={footerLinkClassName}>
                {t('LANDING_NAV_NEWS')}
              </Link>
              <Link to="/contacts" className={footerLinkClassName}>
                {t('LANDING_NAV_CONTACTS')}
              </Link>
            </nav>
          </div>

          {/* Documents */}
          <div className="space-y-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground sm:text-base">
              {t('FOOTER_DOCS_TITLE')}
            </h3>
            <nav aria-label={t('FOOTER_DOCS_TITLE')} className="space-y-2.5">
              <a
                href="/legal/agreement-service-usage-individuals.pdf"
                target="_blank"
                rel="noopener"
                className={footerLinkClassName}
              >
                {t('FOOTER_DOCS_USER_AGREEMENT')}
              </a>
              <a
                href="/legal/policy-personal-data-processing.pdf"
                target="_blank"
                rel="noopener"
                className={footerLinkClassName}
              >
                {t('FOOTER_DOCS_DATA_POLICY')}
              </a>
              <a
                href="/legal/policy-acceptable-use.pdf"
                target="_blank"
                rel="noopener"
                className={footerLinkClassName}
              >
                {t('FOOTER_DOCS_USAGE_POLICY')}
              </a>
              <a
                href="/legal/consent-personal-data-processing.pdf"
                target="_blank"
                rel="noopener"
                className={footerLinkClassName}
              >
                {t('FOOTER_DOCS_DATA_CONSENT')}
              </a>
              <a
                href="/legal/agreement-third-party-transfer.pdf"
                target="_blank"
                rel="noopener"
                className={footerLinkClassName}
              >
                {t('FOOTER_DOCS_THIRD_PARTY')}
              </a>
            </nav>
          </div>

          {/* Contacts */}
          <div className="space-y-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground sm:text-base">
              {t('FOOTER_CONTACTS_TITLE')}
            </h3>
            <div className="space-y-3">
              <a
                href="https://t.me/iqdocai_support"
                target="_blank"
                rel="noopener noreferrer"
                className={footerLinkClassName}
              >
                {t('FOOTER_CONTACTS_TELEGRAM')}
              </a>
              <a href="mailto:admin@iqdoc.ai" className={footerLinkClassName}>
                {t('FOOTER_CONTACTS_EMAIL')}
              </a>
              <a href="mailto:sales@iqdoc.ai" className={footerLinkClassName}>
                {t('FOOTER_CONTACTS_COLLABORATION')}
              </a>
              <a href="mailto:support@iqdoc.ai" className={footerLinkClassName}>
                {t('FOOTER_CONTACTS_SUPPORT')}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-border pt-6 sm:pt-8">
          <div className="flex flex-col items-center justify-between gap-4 text-[10px] text-muted-foreground sm:flex-row sm:text-xs">
            <p>
              {t('FOOTER_DEVELOPED_BY')}{' '}
              <a
                href="https://medach.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center underline transition-colors hover:text-foreground"
              >
                MEDACH
              </a>
            </p>
            <p className="text-center sm:text-right">{t('FOOTER_TAGLINE')}</p>
          </div>
          <p className="mt-4 text-center text-[10px] text-muted-foreground sm:text-xs">
            ООО &quot;Айкьюдок&quot; ИНН 9717183178 ОГРН 1257700388208
          </p>
        </div>
      </div>
    </footer>
  )
}
